import { useState } from 'react';
import { Modal } from './design-system/Modal';
import { ActionButton } from './design-system/ActionButton';
import { submitCardRequest } from '../services/walletShareApi';

interface RequestCardModalProps {
  onClose: () => void;
  initialCardName?: string;
}

export const RequestCardModal: React.FC<RequestCardModalProps> = ({
  onClose,
  initialCardName,
}) => {
  const [cardName, setCardName] = useState(initialCardName ?? '');
  const [cardLink, setCardLink] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!cardName.trim()) {
      setError('Please enter a card name');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitCardRequest(cardName.trim(), cardLink.trim() || undefined);
      setSubmitted(true);
    } catch {
      setError('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCardName('');
    setCardLink('');
    setSubmitted(false);
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={true}
      handleClose={handleClose}
      style={{ maxWidth: 450 }}
      extraFooterButtons={
        !submitted ? (
          <ActionButton
            onClick={() => { handleSubmit(); }}
            variant="primary"
            disabled={isSubmitting || !cardName.trim()}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </ActionButton>
        ) : null
      }
    >
      {submitted ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: 20 }}>Thank you!</h2>
          <p style={{ margin: 0, color: '#666' }}>
            We've received your request and will review it soon.
          </p>
        </div>
      ) : (
        <>
          <h2 style={{ margin: '0 0 8px 0', fontSize: 20 }}>Request a Card</h2>
          <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: 14 }}>
            Can't find your card? Let us know and we'll try to add it.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label
                htmlFor="cardName"
                style={{
                  display: 'block',
                  marginBottom: 6,
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Card Name *
              </label>
              <input
                id="cardName"
                type="text"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                placeholder="e.g. Chase Freedom Unlimited"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 14,
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            </div>

            <div>
              <label
                htmlFor="cardLink"
                style={{
                  display: 'block',
                  marginBottom: 6,
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Link to Card Info{' '}
                <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span>
              </label>
              <input
                id="cardLink"
                type="url"
                value={cardLink}
                onChange={(e) => setCardLink(e.target.value)}
                placeholder="https://..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 14,
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            </div>

            {error && (
              <p style={{ margin: 0, color: '#e53e3e', fontSize: 14 }}>{error}</p>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};
